import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db, storage } from '../../config/firebase';
import { ref as dbRef, push, set, get, update } from 'firebase/database';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import PersonalDetails from './steps/PersonalDetails';
import EducationalDetails from './steps/EducationalDetails';
import DocumentUpload from './steps/DocumentUpload';
import WorkExperience from './steps/WorkExperience';
import CourseDetails from './steps/CourseDetails';
import './AddStudent.css';
import { WorkspaceShell } from '../ui/Workspace';

const AddStudent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const existingStudent = location.state?.existingStudent || null;

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    personalDetails: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      address: '',
    },
    education: {
      olSchool: '',
      olYear: '',
      alSchool: '',
      alYear: '',
      qualifications: [],
    },
    documents: {
      photo: null,
      certificates: [],
    },
    workExperience: [
      {
        company: '',
        position: '',
        duration: '',
        responsibilities: '',
      },
    ],
    joinedCourses: [],
    labels: [],
  });

  useEffect(() => {
    const fetchStudentData = async () => {
      if (existingStudent) {
        const studentRef = dbRef(db, `students/${existingStudent.id}`);
        const snapshot = await get(studentRef);
        const studentData = snapshot.val();

        if (studentData) {
          setFormData({
            personalDetails: {
              firstName: '',
              lastName: '',
              email: '',
              phone: '',
              dateOfBirth: '',
              address: '',
              ...studentData.personalDetails,
            },
            education: {
              olSchool: '',
              olYear: '',
              alSchool: '',
              alYear: '',
              qualifications: [],
              ...studentData.education,
            },
            documents: {
              photo: null,
              certificates: [],
              ...studentData.documents,
              photoURL: studentData.documents?.photoURL || null,
            },
            workExperience: studentData.workExperience || [
              {
                company: '',
                position: '',
                duration: '',
                responsibilities: '',
              },
            ],
            joinedCourses: studentData.joinedCourses || [],
            labels: studentData.labels || [],
          });
        }
      }
    };

    fetchStudentData();
  }, [existingStudent]);

  const steps = [
    'Personal Details',
    'Educational Details',
    'Documents',
    'Work Experience',
    'Course Details',
  ];

  const handleNext = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleSave = async () => {
    try {
      if (currentStep === 1) {
        // Personal Details step
        if (existingStudent) {
          await update(dbRef(db, `students/${existingStudent.id}`), {
            personalDetails: formData.personalDetails,
          });
          alert('Personal details updated successfully!');
        } else {
          const newStudentRef = push(dbRef(db, 'students'));
          await set(newStudentRef, {
            personalDetails: formData.personalDetails,
            documents: { photoURL: null, certificates: [] },
            education: {},
            workExperience: [],
            joinedCourses: [],
          });
          alert('Student added successfully!');
        }
        return;
      }

      if (currentStep === 2) {
        // Educational Details step
        if (existingStudent) {
          await update(dbRef(db, `students/${existingStudent.id}`), {
            education: formData.education,
          });
          alert('Educational details updated successfully!');
          return;
        }
      }

      if (currentStep === 3) {
        // Documents step
        if (existingStudent) {
          let photoURL = null;
          if (formData.documents.photo) {
            const photoRef = storageRef(
              storage,
              `students/${formData.personalDetails.email}/profile-photo`,
            );
            await uploadBytes(photoRef, formData.documents.photo);
            photoURL = await getDownloadURL(photoRef);
          }

          const certificateData = await Promise.all(
            (formData.documents.certificates || []).map(async (file, index) => {
              if (!file) return null;
              if (file.url) return file; // If it's an existing file, keep it as is

              const certRef = storageRef(
                storage,
                `students/${
                  formData.personalDetails.email
                }/certificates/${index}-${file.name || 'file'}`,
              );
              await uploadBytes(certRef, file);
              const url = await getDownloadURL(certRef);
              return {
                url,
                name: file.name,
                label: formData.labels?.[index] || '',
              };
            }),
          ).then((results) => results.filter((item) => item !== null));

          await update(dbRef(db, `students/${existingStudent.id}/documents`), {
            photoURL: photoURL || formData.documents.photoURL, // Keep existing URL if no new photo
            certificates: certificateData,
          });
          alert('Documents updated successfully!');
          return;
        }
      }

      if (currentStep === 4) {
        // Work Experience step
        if (existingStudent) {
          await update(dbRef(db, `students/${existingStudent.id}`), {
            workExperience: formData.workExperience,
          });
          alert('Work experience updated successfully!');
          return;
        }
      }

      // Handle course details step
      if (currentStep === 5) {
        if (existingStudent) {
          await update(dbRef(db, `students/${existingStudent.id}`), {
            joinedCourses: formData.joinedCourses.map((course) => ({
              courseId: course.courseId,
              batchId: course.batchId,
              enrollmentDate: course.enrollmentDate || new Date().toISOString(),
              status: course.status || 'active',
            })),
          });
          alert('Course details updated successfully!');
          return;
        }
      }

      if (existingStudent) {
        await update(dbRef(db, `students/${existingStudent.id}/documents`), {
          photoURL: formData.documents.photoURL,
          certificates: formData.documents.certificates.map((cert) => cert.url),
        });
        alert('Student updated successfully!');
      } else {
        const studentRef = push(dbRef(db, 'students'));
        await set(studentRef, {
          personalDetails: formData.personalDetails,
          education: formData.education,
          workExperience: formData.workExperience,
          documents: {
            photoURL: formData.documents.photoURL,
            certificates: formData.documents.certificates.map(
              (cert) => cert.url,
            ),
          },
          joinedCourses: formData.joinedCourses || [],
        });
        alert('Student added successfully!');
      }

      navigate('/students');
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save. Please try again.');
    }
  };

  const updateFormData = (stepData) => {
    setFormData((prevData) => ({
      ...prevData,
      ...stepData,
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      if (!existingStudent) {
        // For new student
        const studentRef = push(dbRef(db, 'students'));
        await set(studentRef, {
          personalDetails: formData.personalDetails,
          education: formData.education,
          workExperience: formData.workExperience,
          documents: {
            photoURL: formData.documents.photoURL,
            certificates: formData.documents.certificates,
          },
          joinedCourses: formData.joinedCourses.map((course) => ({
            courseId: course.courseId,
            batchId: course.batchId,
            enrollmentDate: course.enrollmentDate || new Date().toISOString(),
            status: course.status || 'active',
          })),
        });
        alert('Student added successfully!');
        navigate('/students');
      } else {
        // For existing student
        await update(dbRef(db, `students/${existingStudent.id}`), {
          personalDetails: formData.personalDetails,
          education: formData.education,
          workExperience: formData.workExperience,
          documents: {
            photoURL: formData.documents.photoURL,
            certificates: formData.documents.certificates,
          },
          joinedCourses: formData.joinedCourses.map((course) => ({
            courseId: course.courseId,
            batchId: course.batchId,
            enrollmentDate: course.enrollmentDate || new Date().toISOString(),
            status: course.status || 'active',
          })),
        });
        alert('Student updated successfully!');
        navigate('/students');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalDetails
            formData={
              formData.personalDetails || {
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                dateOfBirth: '',
                address: '',
              }
            }
            updateFormData={(data) => updateFormData({ personalDetails: data })}
            handleSave={handleSave}
            isEditing={!!existingStudent}
          />
        );
      case 2:
        return (
          <EducationalDetails
            formData={formData.education}
            updateFormData={(data) => updateFormData({ education: data })}
            handleSave={handleSave}
            isEditing={!!existingStudent}
          />
        );
      case 3:
        return (
          <DocumentUpload
            formData={formData.documents}
            updateFormData={(data) => updateFormData({ documents: data })}
            handleSave={handleSave}
            isEditing={!!existingStudent}
            studentId={existingStudent?.id}
          />
        );
      case 4:
        return (
          <WorkExperience
            formData={formData.workExperience}
            updateFormData={(data) => updateFormData({ workExperience: data })}
            handleSave={handleSave}
            isEditing={!!existingStudent}
          />
        );
      case 5:
        return (
          <CourseDetails
            formData={formData}
            updateFormData={updateFormData}
            handleSave={handleSave}
            isEditing={!!existingStudent}
          />
        );
      default:
        return null;
    }
  };

  return (
    <WorkspaceShell
      title={existingStudent ? 'Edit Student' : 'Add New Student'}
      description="Complete each step to register or update a learner profile."
    >
    <div className="add-student-container add-student-container--in-workspace">
      <div className="add-student-header">
        <div className="steps-indicator">
          {steps.map((step, index) => (
            <div
              key={step}
              className={`step ${currentStep === index + 1 ? 'active' : ''} 
                ${currentStep > index + 1 ? 'completed' : ''}`}
            >
              <div className="step-number">{index + 1}</div>
              <div className="step-name">{step}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="form-container">
        {renderStep()}
        <div className="button-container">
          {currentStep > 1 && (
            <button onClick={handlePrevious} className="btn-secondary">
              Previous
            </button>
          )}
          {currentStep < steps.length ? (
            <button onClick={handleNext} className="btn-primary">
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <span className="spinner-border spinner-border-sm mr-2"></span>
                  Submitting...
                </div>
              ) : (
                'Submit'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
    </WorkspaceShell>
  );
};

export default AddStudent;
